'use client';

import { FormField, RequestStatus } from '@/types';
import Altcha from '@components/Altcha';
import Button from '@components/Button';
import FormFields from '@components/Form/FormFields';
import Honeypot from '@components/Honeypot';
import RawHtml from '@components/RawHtml';
import clsx from 'clsx';
import { ReactNode, useId, useState } from 'react';

type Props = {
  formFields: FormField[];
  thankYouText: string;
  className?: string;
  icon?: ReactNode;
};

function WagtailForm(props: Props) {
  const { formFields, thankYouText, className, icon } = props;
  const [status, setStatus] = useState<RequestStatus>('idle');
  const id = useId();

  if (!formFields.length) return null;

  return (
    <form
      id={`form-${id}`}
      className={clsx('form', className)}
      onSubmit={async event => {
        event.preventDefault();

        const formData = new FormData(event.currentTarget);

        const data: {
          [key: string]: FormDataEntryValue | FormDataEntryValue[];
        } = Object.fromEntries(formData);

        formFields.forEach(field => {
          if (field.field_type !== 'checkboxes') return;
          data[field.clean_name] = formData.getAll(field.clean_name);
        });

        try {
          setStatus('loading');
          const res = await fetch(`${process.env.NEXT_PUBLIC_WAGTAIL_API_URL}/v2/forms/`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              page: formFields?.[0]?.page_id,
              form_data: data,
            }),
          });

          if (res.ok) {
            setStatus('success');
          } else {
            setStatus('error');
          }
        } catch (error) {
          setStatus('error');
        }
      }}
    >
      <FormFields formFields={formFields} />
      <Altcha />
      {status === 'success' ? (
        <div className='message message--success'>
          <RawHtml html={thankYouText} />
        </div>
      ) : null}
      {status === 'error' ? (
        <div className='message message--error'>
          <p>Leider ist etwas schief gegangen... Bitte versuchen Sie es später erneut...</p>
          {process.env?.NEXT_PUBLIC_CONTACT_EMAIL ? (
            <p>
              Alternativ können Sie Ihre Anfrage auch direkt an die E-Mail-Adresse{' '}
              <a href={`mailto:${process.env.NEXT_PUBLIC_CONTACT_EMAIL}`}>{process.env.NEXT_PUBLIC_CONTACT_EMAIL}</a>{' '}
              senden.
            </p>
          ) : null}
        </div>
      ) : null}
      <Honeypot />
      <Button primary type='submit' disabled={status === 'loading' || status === 'success'} className='form__submit'>
        {icon} Absenden
      </Button>
    </form>
  );
}

export default WagtailForm;
